// frontend/modules/customer-notification/views/NotificationDashboardView.tsx
import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Space, DatePicker, Row, Col, Divider } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import NotificationMetrics from '../components/dashboard/NotificationMetrics';
import NotificationStatusChart from '../components/dashboard/NotificationStatusChart';
import NotificationActivity from '../components/dashboard/NotificationActivity';
import SmsBalanceCard from '../components/dashboard/SmsBalanceCard';
import { useNotificationStatistics } from '../hooks/useNotificationStatistics';
import { useNotificationHistory } from '../hooks/useNotificationHistory';
import { useNotificationTrigger } from '../hooks/useNotificationTrigger';
import { useSmsBalance } from '../hooks/useSmsBalance';
import moment from "moment";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const NotificationDashboardView: React.FC = () => {
    const { dark } = useTheme();
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);

    // Initialize with last 30 days
    useEffect(() => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        setDateRange([startDate, endDate]);
    }, []);

    // Get statistics data with selected date range
    const {
        statistics,
        isLoading: statsLoading,
        refreshStatistics
    } = useNotificationStatistics(
        dateRange ? dateRange[0] : null,
        dateRange ? dateRange[1] : null
    );

    // Get recent notification history
    const {
        notifications,
        isLoading: historyLoading,
        refreshNotifications
    } = useNotificationHistory();

    // Get SMS balance
    const {
        smsBalance,
        totalAllocation,
        isLoading: smsBalanceLoading,
        refreshSmsBalance
    } = useSmsBalance();

    // Notification trigger functionality
    const { showTriggerForm } = useNotificationTrigger();

    // Handle date range change
    const handleDateRangeChange = (dates: any) => {
        if (dates) {
            setDateRange([dates[0].toDate(), dates[1].toDate()]);
        } else {
            setDateRange(null);
        }
    };

    // Handle refresh button click
    const handleRefresh = () => {
        refreshStatistics();
        refreshNotifications();
        refreshSmsBalance();
    };

    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <div className="flex justify-between items-center">
                        <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                            Notification Dashboard
                        </Title>
                        <Space>
                            <RangePicker
                                // @ts-ignore
                                value={dateRange ? [moment(dateRange[0]), moment(dateRange[1])] : null}
                                onChange={handleDateRangeChange}
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefresh}
                                loading={statsLoading || historyLoading || smsBalanceLoading}
                            >
                                Refresh
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => showTriggerForm('loan-reminder')}
                            >
                                Send Notification
                            </Button>
                        </Space>
                    </div>
                }
            >
                {/* Add SMS Balance Card at the top */}
                <SmsBalanceCard
                    balance={smsBalance}
                    totalAllocation={totalAllocation}
                    isLoading={smsBalanceLoading}
                    onRefresh={refreshSmsBalance}
                />

                <NotificationMetrics
                    statistics={statistics}
                    isLoading={statsLoading}
                />

                <Row gutter={16}>
                    <Col xs={24} sm={24} md={12}>
                        <NotificationStatusChart
                            statistics={statistics}
                            isLoading={statsLoading}
                            chartType="platform"
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <NotificationStatusChart
                            statistics={statistics}
                            isLoading={statsLoading}
                            chartType="category"
                        />
                    </Col>
                </Row>

                <Divider />

                <NotificationActivity
                    notifications={notifications.slice(0, 10)}
                    isLoading={historyLoading}
                />
            </Card>
        </div>
    );
};

export default NotificationDashboardView;